import React from "react";

export default function Security() {
  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-3xl mx-auto py-12 px-4">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Security Policy</h1>
        <p className="text-sm text-gray-500 mb-6">
          <strong>Last Updated:</strong> 4 April 2026<br />
          <strong>Trading Name:</strong> VSENDS<br />
          <strong>Legal Entity:</strong> V CAN RESOURCES LIMITED<br />
          <strong>Company Number:</strong> 13759348<br />
          <strong>Registered Office:</strong> 32 Abbey Drive, Dartford, England, DA2 7WP<br />
          <strong>Support Email:</strong> support@vsends.com<br />
          <strong>Compliance Email:</strong> vg@vcanresources.com
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">1. Introduction</h2>
        <p className="text-gray-700 mb-4">
          V CAN RESOURCES LIMITED ("we", "us", "our") recognises the critical importance of security in protecting the data and trust of our users. This Security Policy outlines the measures we take to protect the VSENDS platform, our infrastructure, and the data processed through our Service.
        </p>
        <p className="text-gray-700 mb-4">
          Security is a shared responsibility. While we implement robust security measures, users also have responsibilities to protect their Accounts and data. This policy describes both our security practices and the steps you can take to enhance your own security.
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">2. Infrastructure Security</h2>
        <p className="text-gray-700 mb-4">
          Our infrastructure is designed and operated with security as a primary consideration:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li>All data is hosted in ISO 27001 certified data centres with 24/7 physical security, biometric access controls, and CCTV monitoring.</li>
          <li>We use redundant, geographically distributed servers to ensure availability and resilience.</li>
          <li>Network traffic is protected by web application firewalls (WAF) and intrusion detection/prevention systems (IDS/IPS).</li>
          <li>DDoS protection is provided through enterprise-grade content delivery networks.</li>
          <li>All infrastructure components are regularly patched and updated to address known vulnerabilities.</li>
        </ul>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">3. Data Encryption</h2>
        <p className="text-gray-700 mb-4">
          We use industry-standard encryption to protect your data both in transit and at rest:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li><strong>Data in transit:</strong> All data transmitted between your browser and our servers is encrypted using TLS 1.2 or higher. We enforce HTTPS across all endpoints and use HSTS to prevent downgrade attacks.</li>
          <li><strong>Data at rest:</strong> All stored data is encrypted using AES-256 encryption. Encryption keys are managed using a dedicated key management service with regular rotation.</li>
          <li><strong>Email content:</strong> Email content stored on our servers is encrypted at rest. We support TLS for email delivery to recipient servers where supported.</li>
        </ul>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">4. Access Controls</h2>
        <p className="text-gray-700 mb-4">
          We implement strict access controls to ensure that only authorised personnel can access systems and data:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li><strong>Role-based access control (RBAC):</strong> Access to systems and data is granted on a need-to-know basis according to job responsibilities.</li>
          <li><strong>Multi-factor authentication (MFA):</strong> All employees are required to use MFA to access internal systems and production environments.</li>
          <li><strong>Principle of least privilege:</strong> Users and systems are granted only the minimum permissions necessary to perform their functions.</li>
          <li><strong>Access reviews:</strong> Access permissions are reviewed regularly and revoked promptly when no longer needed.</li>
          <li><strong>Session management:</strong> User sessions are managed securely with appropriate timeouts and token rotation.</li>
        </ul>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">5. Application Security</h2>
        <p className="text-gray-700 mb-4">
          Our application security practices include:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li>Secure coding practices following OWASP guidelines.</li>
          <li>Regular code reviews and security-focused peer reviews.</li>
          <li>Automated static and dynamic application security testing (SAST/DAST) in our CI/CD pipeline.</li>
          <li>Protection against common web vulnerabilities including SQL injection, cross-site scripting (XSS), cross-site request forgery (CSRF), and clickjacking.</li>
          <li>Input validation and output encoding for all user-supplied data.</li>
          <li>Regular dependency scanning to identify and update vulnerable third-party libraries.</li>
        </ul>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">6. Vulnerability Management</h2>
        <p className="text-gray-700 mb-4">
          We maintain a proactive approach to vulnerability management:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li>Regular vulnerability scanning of all internet-facing systems and internal networks.</li>
          <li>Annual penetration testing conducted by independent, qualified security firms.</li>
          <li>A structured process for triaging, prioritising, and remediating identified vulnerabilities.</li>
          <li>Critical vulnerabilities are addressed within 24 hours; high-severity vulnerabilities within 7 days.</li>
          <li>We maintain a responsible disclosure programme for security researchers to report vulnerabilities.</li>
        </ul>
        <p className="text-gray-700 mb-4">
          If you discover a security vulnerability in our Service, please report it to vg@vcanresources.com. We will acknowledge your report within 48 hours and work to resolve the issue promptly.
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">7. Incident Response</h2>
        <p className="text-gray-700 mb-4">
          We maintain a comprehensive incident response plan to detect, respond to, and recover from security incidents:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li><strong>Detection:</strong> Automated monitoring systems alert our security team to suspicious activity in real time.</li>
          <li><strong>Response:</strong> A dedicated incident response team follows documented procedures to contain and investigate incidents.</li>
          <li><strong>Notification:</strong> Affected users are notified without undue delay in the event of a data breach that poses a risk to their rights and freedoms.</li>
          <li><strong>Recovery:</strong> Systems are restored from secure backups and verified before returning to normal operation.</li>
          <li><strong>Post-incident review:</strong> Every incident is reviewed to identify lessons learned and improve our security posture.</li>
        </ul>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">8. Employee Security</h2>
        <p className="text-gray-700 mb-4">
          Our employees are a critical line of defence in maintaining security:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li>All employees undergo background checks before joining the company.</li>
          <li>Regular security awareness training is provided to all employees, including phishing simulations.</li>
          <li>Employees sign confidentiality agreements and are bound by strict data handling policies.</li>
          <li>Access to production systems and customer data is limited to employees with a legitimate business need.</li>
          <li>Security responsibilities are clearly defined in job descriptions and performance evaluations.</li>
        </ul>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">9. Third-Party Security</h2>
        <p className="text-gray-700 mb-4">
          We carefully manage the security risks associated with third-party vendors and service providers:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li>All third-party processors are assessed for security capabilities before engagement.</li>
          <li>Data processing agreements with security requirements are in place with all vendors that process personal data.</li>
          <li>Regular reviews of third-party security practices and compliance certifications.</li>
          <li>Vendors with access to our systems must comply with our security standards and undergo periodic audits.</li>
        </ul>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">10. Backup and Disaster Recovery</h2>
        <p className="text-gray-700 mb-4">
          We maintain robust backup and disaster recovery capabilities:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li>Automated daily backups of all critical data, encrypted and stored in geographically separate locations.</li>
          <li>Regular testing of backup restoration procedures to ensure data can be recovered reliably.</li>
          <li>A documented disaster recovery plan with defined Recovery Time Objectives (RTO) and Recovery Point Objectives (RPO).</li>
          <li>Annual disaster recovery drills to validate our procedures and identify areas for improvement.</li>
        </ul>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">11. User Security Responsibilities</h2>
        <p className="text-gray-700 mb-4">
          Security is a shared responsibility. We recommend that all users take the following steps to protect their Accounts:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li>Use a strong, unique password for your VSENDS Account.</li>
          <li>Enable two-factor authentication (2FA) on your Account.</li>
          <li>Do not share your login credentials with anyone.</li>
          <li>Regularly review your Account activity and report any suspicious behaviour.</li>
          <li>Keep your devices and browsers up to date with the latest security patches.</li>
          <li>Be cautious of phishing emails that attempt to steal your credentials.</li>
          <li>Use a password manager to generate and store strong passwords.</li>
        </ul>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">12. Compliance and Certifications</h2>
        <p className="text-gray-700 mb-4">
          We are committed to maintaining compliance with relevant security standards and frameworks:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li>Compliance with the UK GDPR and the Data Protection Act 2018.</li>
          <li>Alignment with ISO 27001 information security management principles.</li>
          <li>Compliance with the Payment Card Industry Data Security Standard (PCI DSS) for payment processing.</li>
          <li>Regular independent security audits and assessments.</li>
        </ul>
        <p className="text-gray-700 mb-4">
          We continue to pursue additional certifications and attestations to demonstrate our commitment to security excellence.
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">13. Security Updates and Communication</h2>
        <p className="text-gray-700 mb-4">
          We are committed to transparent communication about security matters:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li>Security advisories are published for any significant vulnerabilities or incidents that may affect users.</li>
          <li>Users are notified of security-relevant changes to the Service through email and in-app notifications.</li>
          <li>We maintain a security status page that provides real-time information about the health and security of our Service.</li>
        </ul>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">14. Contact and Reporting</h2>
        <p className="text-gray-700 mb-4">
          If you have any questions about our security practices or wish to report a security concern, please contact us:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li><strong>Security reports:</strong> vg@vcanresources.com</li>
          <li><strong>General support:</strong> support@vsends.com</li>
          <li><strong>Postal address:</strong> V CAN RESOURCES LIMITED, 32 Abbey Drive, Dartford, England, DA2 7WP</li>
        </ul>
        <p className="text-gray-700 mb-4">
          We take all security reports seriously and will respond promptly. We appreciate the efforts of security researchers who help us keep our platform safe.
        </p>
      </main>
    </div>
  );
}
